import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getMessaging, onMessage } from "firebase/messaging";
import { AudioBase64 } from "./constants/audio";
import { NOTIFICATION_HISTORY_TYPE } from "./constants/status";
import { MODULE_ROUTE } from "./routers/module-router";
import CookieUtils from "./utils/cookie-utils";

export const firebaseConfig = {
  apiKey: "AIzaSyDh6zY5vb6l-FYAvXI_v71sKuxsXCvRIWE",
  authDomain: "scyber-uat.firebaseapp.com",
  projectId: "scyber-uat",
  storageBucket: "scyber-uat.firebasestorage.app",
  messagingSenderId: "49900494966",
  appId: "1:49900494966:web:2b97d402603c1da55abf57",
  measurementId: "G-B74KHMCWL0",
};

export const FIREBASE_VAPID_KEY =
  "BEiOaS-7uCCZLbsMD7YvznynDI4kvB6CmEGW-MnF4YoFwKBqI0g91TABpx2UCqOpS0gV2Brjob4ySne83Jcd5LM";

// Initialize Firebase with async pattern
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getFirestore(app);
const messaging = getMessaging(app);
const token = CookieUtils.getCurrentUser().access_token ?? "";

// Thêm biến để quản lý toast và throttling
let activeToasts: HTMLElement[] = [];
const MAX_TOASTS = 3; // Giới hạn tối đa 3 toast cùng lúc
const notificationQueue: any[] = [];
let isProcessingQueue = false;
const THROTTLE_DELAY = 1000; // 1 giây giữa các notification

// Hàm xử lý queue notification với throttling
const processNotificationQueue = () => {
  if (isProcessingQueue || notificationQueue.length === 0) return;

  isProcessingQueue = true;
  const payload = notificationQueue.shift();

  if (payload) {
    showToastNotification(payload);

    setTimeout(() => {
      isProcessingQueue = false;
      processNotificationQueue(); // Xử lý notification tiếp theo
    }, THROTTLE_DELAY);
  } else {
    isProcessingQueue = false;
  }
};

export const onMessageListener = () =>
  new Promise((resolve) => {
    try {
      if (!messaging) {
        console.warn("Firebase messaging not initialized yet");
        resolve(null);
        return;
      }

      return onMessage(messaging, (payload) => {
        if (token) {
          // Thêm vào queue thay vì hiển thị ngay lập tức
          notificationQueue.push(payload);

          // Bắt đầu xử lý queue nếu chưa đang xử lý
          if (!isProcessingQueue) {
            processNotificationQueue();
          }

          resolve(payload);
        }
      });
    } catch (error) {
      console.error("Error in message listener:", error);
      resolve(null);
    }
  });

export { analytics, db, messaging };

// Hàm hiển thị toast notification từ trên chạy xuống
const showToastNotification = (payload: any) => {
  // Kiểm tra số lượng toast hiện tại
  if (activeToasts.length >= MAX_TOASTS) {
    // Xóa toast cũ nhất
    const oldestToast = activeToasts.shift();
    if (oldestToast && document.body.contains(oldestToast)) {
      oldestToast.style.top = "-100px";
      setTimeout(() => {
        if (document.body.contains(oldestToast)) {
          document.body.removeChild(oldestToast);
        }
      }, 200);
    }
  }

  // Lấy thông tin từ payload
  let type = Number(payload.data?.type);
  const title = payload.notification?.title || "Thông báo mới";
  const message = payload.notification?.body || "Bạn có một thông báo mới";
  let audioBase: string = "";
  switch (type) {
    case NOTIFICATION_HISTORY_TYPE.CREATE_ORDER:
      audioBase = AudioBase64.audioOrder;
      break;
    case NOTIFICATION_HISTORY_TYPE.CREATE_RESERVATION:
      audioBase = AudioBase64.audioBooking;
      break;
    case NOTIFICATION_HISTORY_TYPE.CREATE_COMBO:
      audioBase = AudioBase64.audioCombo;
      break;
    case NOTIFICATION_HISTORY_TYPE.DEPOSIT:
      audioBase = AudioBase64.audioTopup;
      break;
    case NOTIFICATION_HISTORY_TYPE.COMPLETE_ORDER:
      audioBase = AudioBase64.audioOrderComplete;
      break;
    case NOTIFICATION_HISTORY_TYPE.CHAT:
      audioBase = AudioBase64.audioChat;
      break;
    default:
      break;
  }

  // Tạo phần tử toast
  const toast = document.createElement("div");
  toast.className = "toast-notification";
  toast.innerHTML = `
   <div class="toast-content">
    <div class="toast-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
    </div>
    <div class="toast-text">
        <div class="toast-title"><strong>${title}</strong></div>
        <div class="toast-message">${message}</div>
    </div>
    <button type="button" class="toast-close">×</button>
      <audio id="notification-sound" autoplay>
        <source src="data:audio/mp3;base64,${audioBase}" type="audio/mpeg">
      </audio>
</div>
  `;

  // Tính toán vị trí cho toast mới (xếp chồng theo chiều dọc)
  const topPosition = 10 + activeToasts.length * 90; // Mỗi toast cách nhau 90px

  // Thêm CSS cho toast
  toast.style.position = "fixed";
  toast.style.top = "-100px";
  toast.style.right = "10px";
  toast.style.backgroundColor = "white";
  toast.style.padding = "10px";
  toast.style.borderRadius = "8px";
  toast.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";
  toast.style.minWidth = "250px";
  toast.style.maxWidth = "350px";
  toast.style.zIndex = "9999";
  toast.style.transition = "top 0.5s ease";

  // CSS cho layout bên trong toast
  const toastContent = toast.querySelector(".toast-content");
  if (toastContent) {
    (toastContent as HTMLElement).style.display = "flex";
    (toastContent as HTMLElement).style.justifyContent = "space-between";
    (toastContent as HTMLElement).style.alignItems = "flex-start";
  }

  // CSS cho icon
  const toastIcon = toast.querySelector(".toast-icon");
  if (toastIcon) {
    (toastIcon as HTMLElement).style.marginRight = "12px";
    (toastIcon as HTMLElement).style.color = "#3b82f6"; // Màu xanh dương
    (toastIcon as HTMLElement).style.display = "flex";
    (toastIcon as HTMLElement).style.alignItems = "center";
    (toastIcon as HTMLElement).style.justifyContent = "center";
  }
  // CSS cho phần text
  const toastText = toast.querySelector(".toast-text");
  if (toastText) {
    (toastText as HTMLElement).style.flexGrow = "1";
    (toastText as HTMLElement).style.marginRight = "10px";
    (toastText as HTMLElement).style.fontSize = "12";
    (toastText as HTMLElement).style.color = "#7D7E81";
    (toastText as HTMLElement).style.fontFamily = "Nunito, sans-serif";
  }

  // CSS cho tiêu đề
  const toastTitle = toast.querySelector(".toast-title");
  if (toastTitle) {
    (toastTitle as HTMLElement).style.fontSize = "12";
    (toastTitle as HTMLElement).style.color = "#7D7E81";
    (toastTitle as HTMLElement).style.fontFamily = "Nunito, sans-serif";
  }

  // CSS cho nút đóng
  const closeButton = toast.querySelector(".toast-close");
  if (closeButton) {
    (closeButton as HTMLElement).style.backgroundColor = "transparent";
    (closeButton as HTMLElement).style.border = "none";
    (closeButton as HTMLElement).style.fontSize = "20px";
    (closeButton as HTMLElement).style.cursor = "pointer";
    (closeButton as HTMLElement).style.padding = "0 5px";
    (closeButton as HTMLElement).style.marginLeft = "auto";
    (closeButton as HTMLElement).style.lineHeight = "1";
  }

  // Thêm toast vào danh sách active
  activeToasts.push(toast);

  // Thêm toast vào body
  document.body.appendChild(toast);

  // Hiệu ứng trượt xuống với vị trí được tính toán
  setTimeout(() => {
    toast.style.top = `${topPosition}px`;
  }, 10);

  // Hàm xóa toast và cập nhật vị trí các toast còn lại
  const removeToast = () => {
    const index = activeToasts.indexOf(toast);
    if (index > -1) {
      activeToasts.splice(index, 1);
      // Cập nhật lại vị trí các toast còn lại
      activeToasts.forEach((t, i) => {
        t.style.top = `${10 + i * 90}px`;
      });
    }
    removeToastFromDOM(toast);
  };

  // Hàm helper để xóa toast khỏi DOM
  const removeToastFromDOM = (toastElement: HTMLElement) => {
    toastElement.style.top = "-100px";
    setTimeout(() => {
      if (document.body.contains(toastElement)) {
        document.body.removeChild(toastElement);
      }
    }, 200);
  };

  toast.addEventListener("click", (event) => {
    // Chỉ điều hướng nếu không phải nhấn vào nút đóng
    if (type !== NOTIFICATION_HISTORY_TYPE.CHAT) {
      window.location.href = MODULE_ROUTE.EMPLOYEE_NOTIFICATION_HISTORY;
    }

    if (!(event.target as HTMLElement).closest(".toast-close")) {
      removeToast();
    }
  });

  // Xử lý sự kiện nút đóng
  if (closeButton) {
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation(); // Ngăn chặn event bubbling
      removeToast();
    });
  }

  // Tự động đóng toast sau 5 giây
  setTimeout(() => {
    if (document.body.contains(toast)) {
      removeToast();
    }
  }, 5000);
};
